import { useCallback, useEffect, useRef } from 'react'

import {
  LOCAL_STORAGE_NAME_JWT_ACCESS,
  LOCAL_STORAGE_NAME_JWT_ACCESS_EXPIRATION_TIME,
  LOCAL_STORAGE_NAME_JWT_REFRESH,
} from '../../constants/storage'
import { getStorageItem, removeStorageItem } from '../../utils/storage'

import { getJWT, refreshJWT } from './useJWT.api'
import { persistRefreshedJWT } from './useJWT.utils'

export function useJWT() {
  const checkTokenTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      clearTimeout(checkTokenTimeoutRef.current)
    }
  }, [])

  const setTokenCallback = useCallback(data => {
    const expTime = persistRefreshedJWT(data)

    // eslint-disable-next-line react-hooks/immutability -- setTokenCallback and checkRefreshToken are mutually recursive useCallbacks; this forward reference is intentional and a reorder cannot satisfy both directions
    checkTokenTimeoutRef.current = setTimeout(checkRefreshToken, expTime)
  }, [])

  const checkRefreshToken = useCallback(() => {
    const accessExpirationTime = getStorageItem(
      LOCAL_STORAGE_NAME_JWT_ACCESS_EXPIRATION_TIME
    )
    const refreshToken = getStorageItem(LOCAL_STORAGE_NAME_JWT_REFRESH)

    if (!accessExpirationTime || Date.now() >= +accessExpirationTime) {
      clearTimeout(checkTokenTimeoutRef.current)

      refreshJWT({ refresh: refreshToken })
        .then(({ data }) => {
          setTokenCallback(data)
        })
        .catch(() => {
          getJWT().then(({ data }) => {
            setTokenCallback(data)
          })
        })
    } else {
      // If access expiration time is valid, set timeout to check after expiration
      checkTokenTimeoutRef.current = setTimeout(
        checkRefreshToken,
        accessExpirationTime - Date.now()
      )
    }
  }, [])

  const checkJWT = useCallback(() => {
    if (!getStorageItem(LOCAL_STORAGE_NAME_JWT_ACCESS_EXPIRATION_TIME)) {
      getJWT().then(({ data }) => {
        setTokenCallback(data)
      })
    } else {
      checkRefreshToken()
    }
  }, [])

  const removeJWT = useCallback(() => {
    removeStorageItem(LOCAL_STORAGE_NAME_JWT_ACCESS)
    removeStorageItem(LOCAL_STORAGE_NAME_JWT_ACCESS_EXPIRATION_TIME)
    removeStorageItem(LOCAL_STORAGE_NAME_JWT_REFRESH)
  }, [])

  return { checkJWT, checkTokenTimeoutRef, removeJWT }
}
